/**
 * FAQ 독립 페이지
 * - 기존 faqService 재사용
 * - 아코디언 Q&A
 * - 검색 기능
 * - SEO 최적화
 */
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, TextField, InputAdornment, Skeleton,
  Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import { Search, ExpandMore, HelpOutline } from '@mui/icons-material';
import AppBarSection from './components/main/AppBarSection';
import Footer from './components/main/Footer';
import SupportTabNav from './components/common/SupportTabNav';
import { getAllFAQs } from '../services/faqService';
import type { FAQ } from '../types/faq.types';

export default function FaqPage() {
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [expanded, setExpanded] = useState<string | false>(false);

  useEffect(() => {
    document.title = 'FAQ | 다원트레이드';
    getAllFAQs({ activeOnly: true, sortOrder: 'asc' })
      .then(setFaqs)
      .finally(() => setLoading(false));
  }, []);

  const filtered = searchQuery.trim()
    ? faqs.filter(f =>
        f.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
        f.answer.toLowerCase().includes(searchQuery.toLowerCase()))
    : faqs;

  const handleAccordionChange = (id: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? id : false);
  };

  // 검색어 하이라이트
  const highlight = (text: string) => {
    if (!searchQuery.trim()) return text;
    const parts = text.split(new RegExp(`(${searchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));
    return parts.map((part, i) =>
      part.toLowerCase() === searchQuery.toLowerCase()
        ? <Box component="mark" key={i} sx={{ bgcolor: '#fef08a', px: 0.3, borderRadius: 0.5 }}>{part}</Box>
        : part
    );
  };

  return (
    <Box sx={{ minHeight: '100vh', background: '#fff', display: 'flex', flexDirection: 'column' }}>
      <AppBarSection />

      <Box sx={{ maxWidth: { xs: '100%', md: '50%' }, mx: 'auto', px: { xs: 2, md: 0 }, flex: 1, width: '100%' }}>
        <SupportTabNav active="faq" />

        {/* 타이틀 */}
        <Box sx={{ textAlign: 'center', mb: { xs: 2, md: 3 } }}>
          <Typography fontWeight={800} sx={{ fontSize: { xs: '1.5rem', md: '2rem' }, color: '#0f172a', letterSpacing: '-0.02em' }}>
            FAQ
          </Typography>
          <Typography sx={{ fontSize: '0.9rem', color: '#64748b', mt: 0.5 }}>
            자주 묻는 질문을 확인하세요
          </Typography>
        </Box>

        {/* 검색 */}
        <Box sx={{ mb: { xs: 3, md: 4 }, maxWidth: 480, mx: 'auto' }}>
          <TextField
            fullWidth
            placeholder="질문을 검색하세요..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: '#94a3b8' }} />
                </InputAdornment>
              ),
              sx: {
                borderRadius: 50,
                bgcolor: '#f8fafc',
                '& fieldset': { borderColor: '#e5e7eb' },
                '&:hover fieldset': { borderColor: '#cbd5e1 !important' },
                '&.Mui-focused fieldset': { borderColor: '#3b82f6 !important' },
              },
            }}
            size="small"
          />
        </Box>

        {/* FAQ 목록 */}
        <Box sx={{ mb: 6 }}>
          {loading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} variant="rounded" height={56} sx={{ borderRadius: 2 }} />)}
            </Box>
          ) : filtered.length === 0 ? (
            <Box sx={{ py: 8, textAlign: 'center' }}>
              <HelpOutline sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
              <Typography sx={{ color: '#94a3b8' }}>
                {searchQuery ? `'${searchQuery}'에 대한 검색 결과가 없습니다` : '등록된 FAQ가 없습니다'}
              </Typography>
            </Box>
          ) : (
            filtered.map((faq) => (
              <Accordion
                key={faq.id}
                expanded={expanded === faq.id}
                onChange={handleAccordionChange(faq.id)}
                disableGutters
                elevation={0}
                sx={{
                  borderBottom: '1px solid #f0f0f0',
                  '&::before': { display: 'none' },
                  '&.Mui-expanded': { margin: 0 },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore sx={{ color: '#3b82f6', fontSize: 22 }} />}
                  sx={{
                    px: { xs: 1, md: 2 },
                    py: 0.5,
                    '&:hover': { bgcolor: '#fafbfc' },
                    '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1.5, my: 1.5 },
                  }}
                >
                  <Box sx={{
                    width: 32, height: 32, borderRadius: '50%',
                    bgcolor: expanded === faq.id ? '#0f172a' : '#f1f5f9',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                    transition: 'all 0.2s',
                  }}>
                    <Typography sx={{
                      fontSize: '0.75rem', fontWeight: 800,
                      color: expanded === faq.id ? '#fff' : '#94a3b8',
                    }}>
                      ?
                    </Typography>
                  </Box>
                  <Typography sx={{ fontWeight: 600, fontSize: { xs: '0.88rem', md: '0.95rem' }, color: '#0f172a' }}>
                    {highlight(faq.question)}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: { xs: 1, md: 2 }, pb: 3, pt: 0, pl: { xs: 6, md: 7.5 } }}>
                  <Typography sx={{
                    fontSize: { xs: '0.82rem', md: '0.88rem' },
                    color: '#475569',
                    lineHeight: 1.8,
                    whiteSpace: 'pre-wrap',
                  }}>
                    {highlight(faq.answer)}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))
          )}
        </Box>
      </Box>

      <Footer />
    </Box>
  );
}
